import {
  AddCircle,
  BarChartOutlined,
  ChevronRight,
  Delete,
  MoreVert,
  RestartAlt,
  Share,
} from '@mui/icons-material';
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { FC, MouseEvent, useState } from 'react';

import { IAudience } from '@/interfaces';

interface AudienceCardMenuProps {
  audience: IAudience;
  onDeleteAudience: () => void;
  onBuildAudience: (audience: IAudience) => void;
  onRebuildAudience: (audience: IAudience) => void;
  onShowInsight: (id: number | undefined) => void;
  onShareAudience: (audience: IAudience) => void;
  onShowActivations: (id: number | undefined) => void;
  onCreateActivation: (id: number | undefined) => void;
}

const AudienceCardMenu: FC<AudienceCardMenuProps> = ({
  audience,
  onRebuildAudience,
  onDeleteAudience,
  onShareAudience,
  onBuildAudience,
  onShowInsight,
  onShowActivations,
  onCreateActivation,
}) => {
  const audienceSharedToUser =
    audience.shared && audience.shared_with?.length === 0;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [audienceSubmenuEl, setAudienceSubmenuEl] =
    useState<null | HTMLElement>(null);
  const [insightSubmenuEl, setInsightSubmenuEl] = useState<null | HTMLElement>(
    null
  );
  const [activationSubmenuEl, setActivationSubmenuEl] =
    useState<null | HTMLElement>(null);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setAudienceSubmenuEl(null);
    setInsightSubmenuEl(null);
    setActivationSubmenuEl(null);
  };

  const handleOpenAudienceSubmenu = (event: MouseEvent<HTMLElement>) =>
    setAudienceSubmenuEl(event.currentTarget);

  const handleCloseAudienceSubmenu = () => setAudienceSubmenuEl(null);

  const handleOpenInsightSubmenu = (event: MouseEvent<HTMLElement>) =>
    setInsightSubmenuEl(event.currentTarget);

  const handleOpenActivationSubmenu = (event: MouseEvent<HTMLElement>) =>
    setActivationSubmenuEl(event.currentTarget);

  const handleDeleteAudience = () => {
    onDeleteAudience();
    handleCloseMenu();
  };

  const handleRebuildAudience = () => {
    onRebuildAudience(audience);
    handleCloseMenu();
  };

  const handleShareAudience = () => {
    onShareAudience(audience);
    handleCloseMenu();
  };

  const handleCreateActivation = () => {
    onCreateActivation(audience.id);
    handleCloseMenu();
  };

  const handleViewActivations = () => {
    onShowActivations(audience.id);
    handleCloseMenu();
  };

  return (
    <>
      {/* Main Menu Button */}
      <Tooltip title="Audience Actions">
        <IconButton
          onClick={handleOpenMenu}
          sx={{ p: 0.5, borderRadius: '6px' }}
        >
          <MoreVert color={'primary'} />
        </IconButton>
      </Tooltip>

      {/* Main Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {/* Audience Submenu */}
        <MenuItem
          onClick={handleOpenAudienceSubmenu}
          disabled={audienceSharedToUser}
        >
          <ListItemText primary="Audience" />
          <ListItemIcon>
            <ChevronRight />
          </ListItemIcon>
        </MenuItem>

        {/* Audience Options */}
        <Menu
          anchorEl={audienceSubmenuEl}
          open={Boolean(audienceSubmenuEl)}
          onClose={handleCloseAudienceSubmenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem onClick={handleRebuildAudience}>
            <ListItemIcon>
              <RestartAlt color="primary" />
            </ListItemIcon>
            <ListItemText primary="Rebuild Audience" />
          </MenuItem>
          <MenuItem onClick={handleDeleteAudience}>
            <ListItemIcon>
              <Delete color="primary" />
            </ListItemIcon>
            <ListItemText primary="Delete Audience" />
          </MenuItem>
          <MenuItem onClick={handleShareAudience}>
            <ListItemIcon>
              <Share color="primary" />
            </ListItemIcon>
            <ListItemText primary="Share Audience" />
          </MenuItem>
        </Menu>

        <Divider />

        {/* Insight Submenu */}
        <MenuItem
          onClick={handleOpenInsightSubmenu}
          disabled={!audience.run_id}
        >
          <ListItemText primary="Insight" />
          <ListItemIcon>
            <ChevronRight />
          </ListItemIcon>
        </MenuItem>

        {/* Insight Options */}
        <Menu
          anchorEl={insightSubmenuEl}
          open={Boolean(insightSubmenuEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              onShowInsight(audience.id);
            }}
          >
            <ListItemIcon>
              <BarChartOutlined color="primary" />
            </ListItemIcon>
            <ListItemText primary="View Insight" />
          </MenuItem>
          <MenuItem
            disabled={audienceSharedToUser}
            onClick={() => {
              handleCloseMenu();
              onBuildAudience(audience);
            }}
          >
            <ListItemIcon>
              <RestartAlt color="primary" />
            </ListItemIcon>
            <ListItemText primary="Rebuild Insight" />
          </MenuItem>
        </Menu>

        <Divider />

        {/* Activation Submenu */}
        <MenuItem
          onClick={handleOpenActivationSubmenu}
          disabled={!audience.run_id}
        >
          <ListItemText primary="Activation" />
          <ListItemIcon>
            <ChevronRight />
          </ListItemIcon>
        </MenuItem>

        {/* Activation Options */}
        <Menu
          anchorEl={activationSubmenuEl}
          open={Boolean(activationSubmenuEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              handleViewActivations();
            }}
          >
            <ListItemIcon>
              <BarChartOutlined color="primary" />
            </ListItemIcon>
            <ListItemText primary="View Activations" />
          </MenuItem>
          <MenuItem
            disabled={audienceSharedToUser}
            onClick={() => {
              handleCloseMenu();
              handleCreateActivation();
            }}
          >
            <ListItemIcon>
              <AddCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="Create Activation" />
          </MenuItem>
        </Menu>
      </Menu>
    </>
  );
};

export default AudienceCardMenu;
